/* eslint-disable require-await */
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

import { Loggable } from '../../../utils/logging/loggable';
import { DEFAULT_GROUP_SHARE_TIMEOUTS } from '../state/app';

import { PresentationAudioService } from './presentation/presentation-audio.service';

export enum LoadingTransitionLabel {
  ALL = 'Presenting to the Scootaverse',
  SINGLE = 'Presenting to the Room',
  AUDIENCE = 'Presentation Starting',
  GROUPSHARE = 'Group Share Starting',
  GROUPSHARE_HOST = 'Presenting to Group',
}

@Injectable({
  providedIn: 'root',
})
export class TransitionService extends Loggable implements OnDestroy {
  private presentationAudioService = inject(PresentationAudioService);

  public showDarkMode = false;
  public readonly showOverlay = signal(false);
  public readonly showFadeBackground = signal(false);
  public readonly showFadeInBackground = signal(false);
  private readonly _overlayPresentationText = signal<string>('');

  // Indicates that the background is altered (faded out)
  public isAltered = false;

  private _isTransitioning: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isTransitioning$: Observable<boolean> = this._isTransitioning.asObservable();

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('TransitionService');
  }

  /**
   * Enable Dark mode effect
   *
   * @param {string} overlayText Text displayed in the transition screen (if it will be called), should be member of LoadingTransitionLabel
   * @param {boolean} connectOverlay Should show a overlay screen after dark mode
   * @param {boolean} fadeOutBackground  Background should fade out before effect
   */
  public async enableDarkModeEffect(
    overlayText: string,
    connectOverlay: boolean,
    fadeOutBackground: boolean,
  ): Promise<boolean> {
    this._isTransitioning.next(true);

    if (fadeOutBackground) {
      await this.enableFadeOutBackground(true);
    } else {
      this.showDarkMode = true;
    }
    // Wait for dark mode to set in
    await new Promise((resolve) => {
      setTimeout(async () => {
        // Jumps directly to overlay loading if requested - with false fadeout since we already faded the background here
        if (connectOverlay) {
          await this.enableTransitionOverlay(overlayText, false, true);
        }
        this.showDarkMode = false;
        this._isTransitioning.next(false);
        resolve(true);
      }, DEFAULT_GROUP_SHARE_TIMEOUTS.darkModeTimeout);
    });
    return true;
  }

  /**
   * Enable Transition screen
   *
   * @param {string} overlayText Text displayed in the transition screen, should be member of LoadingTransitionLabel
   * @param {boolean} fadeOutBackground Background should fade out before transition
   * @param {boolean} isAudience Should use audience transition timing
   */
  public enableTransitionOverlayTimeAudience =
    DEFAULT_GROUP_SHARE_TIMEOUTS.backgroundFadeAudienceTransition +
    DEFAULT_GROUP_SHARE_TIMEOUTS.presentationAudienceTransition;
  public enableTransitionOverlayTimePresenter =
    DEFAULT_GROUP_SHARE_TIMEOUTS.backgroundFadePresenterTransition +
    DEFAULT_GROUP_SHARE_TIMEOUTS.backgroundFadeAudienceTransition;
  public async enableTransitionOverlay(
    overlayText: string,
    fadeOutBackground: boolean,
    isAudience = false,
  ): Promise<boolean> {
    this._overlayPresentationText.set(overlayText);
    this._isTransitioning.next(true);

    // Adds fade out for background if requested
    if (fadeOutBackground) {
      await this.enableFadeOutBackground(isAudience);
    }
    this.showOverlay.set(true);

    // Hold transition for some time
    await new Promise((resolve) => {
      setTimeout(
        async () => {
          this.showFadeBackground.set(false);
          this.showOverlay.set(false);
          this._isTransitioning.next(false);
          this.presentationAudioService.resetFadedAudioMix();
          resolve(true);
        },
        isAudience
          ? DEFAULT_GROUP_SHARE_TIMEOUTS.presentationAudienceTransition
          : DEFAULT_GROUP_SHARE_TIMEOUTS.presentationPresenterTransition,
      );
    });
    return true;
  }

  public async enableFadeOutBackground(isAudience: boolean): Promise<void> {
    this.showFadeBackground.set(true);

    await new Promise((resolve) => {
      setTimeout(
        async () => {
          this.isAltered = true;
          resolve(true);
        },
        isAudience
          ? DEFAULT_GROUP_SHARE_TIMEOUTS.backgroundFadeAudienceTransition
          : DEFAULT_GROUP_SHARE_TIMEOUTS.backgroundFadePresenterTransition,
      );
    });
  }

  public async enableFadeInBackground(): Promise<void> {
    if (this.isAltered) {
      this.showFadeInBackground.set(true);

      await new Promise((resolve) => {
        setTimeout(async () => {
          this.showFadeInBackground.set(false);
          this.isAltered = false;
          resolve(true);
        }, 1500);
      });
    }
  }

  public set overlayPresentationText(t: string) {
    this._overlayPresentationText.set(t);
  }

  public get overlayPresentationText(): string {
    return this._overlayPresentationText();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
